// frontend/src/widgets/TermsAndConditionsWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Automatic Number Plate Recognition
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-02-04
// Updated:      2026-09-09 15:53 BST
//
// Description:
//   Terms & Conditions manager (tenant-safe).
//
//   IMPORTANT:
//     - Renders CONTENT ONLY (NO WidgetWrapper / NO title / NO close button).
//     - Hosting page/dashboard WidgetWrapper provides title + close.
//
// Fixes (2026-02-05):
//   - Restores input focus (no preventDefault).
//   - Cancels react-grid-layout drag capture using stopPropagation ONLY on interactive controls.
//   - New Version honours "one default per key":
//       • create new row with is_default=false
//       • if user requested default -> unset current default, then set new as default
//   - Persists last-used key so "new key" shows after refresh.
//   - Surfaces backend errors verbosely.
//
// Update (2026-02-05):
//   - Supports BOTH quote-linked term types via preset dropdown:
//       • Quote Terms        (key: quotes_terms)
//       • Service Terms      (key: service_terms)
//
// Update (2026-02-06):
//   - Replaces markdown textarea with Tiptap WYSIWYG (stores HTML into body_md).
//   - Backwards compatible: if existing body_md is markdown, it is displayed via a basic md->html transform.
//   - Output is still persisted to body_md (string) so backend/schema do not need changes.
//
// Notes:
//   - Uses shared Axios instance (axiosInstance).
//   - Tries both "terms" and "/api/terms" to tolerate baseURL differences.
//
// Mobile Responsiveness (2026-09-09):
//   - Preserves all Tiptap editor behaviour, RGL stopPropagation handling,
//     draft persistence, version/default rules and API fallback paths.
//   - Desktop retains the existing 320px Versions pane + flexible Editor pane.
//   - Mobile stacks Versions above Editor using the same render tree.
//   - The mobile Versions list shows one complete version card per internal
//     vertical scroll/snap.
//   - The WYSIWYG editor and both panes use natural mobile height; no fixed
//     desktop-height clipping is carried into the phone layout.
//   - Dynamic mobile sizing uses the proven Vision pattern:
//       restore previous host sizing;
//       clear temporary version-list/root sizing;
//       measure the natural first version card;
//       size the version viewport to one complete card;
//       wait a second animation frame;
//       resize the first width-matching fixed-height mobile host.
//   - ResizeObserver watches the complete content and first version card so
//     editor/version content changes cannot leave the widget clipped.
//   - Editor toolbar, selectors, checkboxes and actions become touch-safe on mobile.
//   - No duplicate mobile render tree is mounted.
//
// Operational Notes:
//   - No API routes, payloads, localStorage keys, term-version semantics or
//     editor persistence behaviour are changed by this responsive pass.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";

// Tiptap
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";

const DEFAULT_KEY = "quotes_terms";
const SERVICE_DEFAULT_KEY = "service_terms";

const LAST_KEY_STORAGE = "termsDocuments:lastKey";
const LAST_PRESET_STORAGE = "termsDocuments:lastPreset";

const TERMS_KEY_PRESETS = [
  { id: "quote_terms", label: "Quote Terms", key: DEFAULT_KEY },
  { id: "service_terms", label: "Service Terms", key: SERVICE_DEFAULT_KEY },
];

const norm = (v) => String(v ?? "").trim();

const gbDateTime = (v) => {
  if (!v) return "";
  try {
    const d = new Date(v);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(v);
  }
};

function describeAxiosError(e) {
  const status = e?.response?.status;
  const data = e?.response?.data;
  const detail = data?.detail;
  const msg = e?.message;

  if (status) {
    if (typeof detail === "string" && detail) return `HTTP ${status}: ${detail}`;
    if (detail) return `HTTP ${status}: ${JSON.stringify(detail)}`;
    if (data) return `HTTP ${status}: ${JSON.stringify(data)}`;
    return `HTTP ${status}`;
  }
  if (detail) return typeof detail === "string" ? detail : JSON.stringify(detail);
  if (data) return JSON.stringify(data);
  return msg || "Request failed";
}

async function tryPaths(fn, paths) {
  let lastErr = null;
  for (const p of paths) {
    try {
      const res = await fn(p);
      return { data: res?.data, usedPath: p };
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("All paths failed");
}

// Stop RGL drag capture WITHOUT breaking focus (no preventDefault)
const stopGridDrag = (e) => {
  try {
    e.stopPropagation?.();
  } catch {}
};

const isProbablyHtml = (s) => {
  const v = String(s || "").trim();
  if (!v) return false;
  return v.startsWith("<") && v.includes(">") && /<\/?[a-z][\s\S]*>/i.test(v);
};

// Minimal md -> html for legacy docs (good enough for headings/lists/paragraphs)
const mdToHtmlBasic = (md) => {
  let s = String(md || "");
  s = s.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // fenced code blocks -> <pre><code>
  s = s.replace(/```[^\n]*\n([\s\S]*?)\n```/g, (_m, code) => `<pre><code>${escapeHtml(code)}</code></pre>`);

  const lines = s.split("\n");
  let out = [];
  let inUl = false;
  let inOl = false;

  const closeLists = () => {
    if (inUl) {
      out.push("</ul>");
      inUl = false;
    }
    if (inOl) {
      out.push("</ol>");
      inOl = false;
    }
  };

  for (let raw of lines) {
    const line = raw ?? "";
    const t = line.trim();

    if (!t) {
      closeLists();
      out.push("<p></p>");
      continue;
    }

    // headings
    const hm = t.match(/^(#{1,4})\s+(.*)$/);
    if (hm) {
      closeLists();
      const lvl = hm[1].length;
      const body = inlineMdToHtml(hm[2] || "");
      out.push(`<h${lvl}>${body}</h${lvl}>`);
      continue;
    }

    // ordered list
    const om = t.match(/^(\d+)\.\s+(.*)$/);
    if (om) {
      if (inUl) {
        out.push("</ul>");
        inUl = false;
      }
      if (!inOl) {
        out.push("<ol>");
        inOl = true;
      }
      out.push(`<li>${inlineMdToHtml(om[2] || "")}</li>`);
      continue;
    }

    // unordered list
    const um = t.match(/^[-*+]\s+(.*)$/);
    if (um) {
      if (inOl) {
        out.push("</ol>");
        inOl = false;
      }
      if (!inUl) {
        out.push("<ul>");
        inUl = true;
      }
      out.push(`<li>${inlineMdToHtml(um[1] || "")}</li>`);
      continue;
    }

    closeLists();
    out.push(`<p>${inlineMdToHtml(t)}</p>`);
  }

  closeLists();
  return out.join("\n");
};

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const inlineMdToHtml = (s) => {
  let v = escapeHtml(s);

  // links [text](url)
  v = v.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, txt, url) => {
    const t = String(txt || "");
    const u = String(url || "").replace(/"/g, "");
    return `<a href="${u}">${escapeHtml(t)}</a>`;
  });

  // bold / italic / underline-ish (best effort)
  v = v.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  v = v.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  v = v.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  v = v.replace(/_([^_]+)_/g, "<em>$1</em>");
  v = v.replace(/`([^`]+)`/g, "<code>$1</code>");

  // line breaks
  v = v.replace(/\n/g, "<br/>");
  return v;
};

function ToolbarButton({ onClick, disabled, children, title }) {
  return (
    <button
      type="button"
      className="min-h-[44px] rounded-lg border px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-60 md:min-h-0"
      onMouseDown={stopGridDrag}
      onPointerDown={stopGridDrag}
      onTouchStart={stopGridDrag}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

function RichEditor({ value, onChange, disabled }) {
  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3, 4] },
        }),
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
        }),
      ],
      content: value || "",
      editable: !disabled,
      onUpdate({ editor }) {
        const html = editor.getHTML();
        onChange?.(html);
      },
    },
    // IMPORTANT: key is handled outside; this instance stays stable unless unmounted
    []
  );

  // keep editor in sync when doc selection changes
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = String(value || "");
    if (current !== next) {
      editor.commands.setContent(next || "<p></p>", false);
    }
  }, [editor, value]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const href = window.prompt("Link URL", prev);
    if (href === null) return;
    const url = String(href || "").trim();
    if (!url) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url, target: "_blank", rel: "noopener noreferrer" }).run();
  }, [editor]);

  return (
    <div className="min-w-0 rounded-lg border bg-white" onMouseDown={stopGridDrag} onPointerDown={stopGridDrag} onTouchStart={stopGridDrag}>
      <div className="flex flex-wrap items-center gap-1 border-b px-2 py-2 sm:gap-2">
        <ToolbarButton
          disabled={!editor || disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          title="Heading 1"
        >
          H1
        </ToolbarButton>
        <ToolbarButton
          disabled={!editor || disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Heading 2"
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          disabled={!editor || disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          title="Heading 3"
        >
          H3
        </ToolbarButton>
        <ToolbarButton
          disabled={!editor || disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
          title="Heading 4"
        >
          H4
        </ToolbarButton>

        <div className="mx-1 h-5 w-px bg-gray-200" />

        <ToolbarButton disabled={!editor || disabled} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
          <b>B</b>
        </ToolbarButton>
        <ToolbarButton disabled={!editor || disabled} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
          <i>I</i>
        </ToolbarButton>
        <ToolbarButton disabled={!editor || disabled} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
          <u>U</u>
        </ToolbarButton>

        <div className="mx-1 h-5 w-px bg-gray-200" />

        <ToolbarButton
          disabled={!editor || disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bulleted list"
        >
          • List
        </ToolbarButton>
        <ToolbarButton
          disabled={!editor || disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Numbered list"
        >
          1. List
        </ToolbarButton>

        <div className="mx-1 h-5 w-px bg-gray-200" />

        <ToolbarButton disabled={!editor || disabled} onClick={() => editor.chain().focus().setHardBreak().run()} title="Line break">
          ↵
        </ToolbarButton>
        <ToolbarButton disabled={!editor || disabled} onClick={setLink} title="Set link">
          Link
        </ToolbarButton>
        <ToolbarButton
          disabled={!editor || disabled}
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          title="Clear formatting"
        >
          Clear
        </ToolbarButton>
      </div>

      <div className="vx-terms-editor-body px-3 py-3" style={{ minHeight: 420 }}>
        <EditorContent
          editor={editor}
          className="prose max-w-none break-words text-sm"
          style={{ fontFamily: "var(--font-body)" }}
        />
      </div>
    </div>
  );
}

export default function TermsAndConditionsWidget() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const widgetRootRef = useRef(null);
  const contentRef = useRef(null);
  const versionsViewportRef = useRef(null);
  const firstVersionCardRef = useRef(null);

  // Preset for the two supported term types (Quote Terms / Service Terms)
  const [presetId, setPresetId] = useState("quote_terms");

  const [key, setKey] = useState(DEFAULT_KEY);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const selected = useMemo(() => items.find((x) => x.id === selectedId) || null, [items, selectedId]);

  const maxVersion = useMemo(() => {
    let m = 0;
    for (const it of items) {
      const v = Number(it?.version || 0);
      if (Number.isFinite(v) && v > m) m = v;
    }
    return m;
  }, [items]);

  const currentDefault = useMemo(() => items.find((x) => x?.is_default) || null, [items]);

  // Controlled form state + refs for submit accuracy
  const [form, setForm] = useState({
    title: "",
    body_md: "",
    is_active: true,
    is_default: false,
  });

  const titleRef = useRef(null);
  const activeRef = useRef(null);
  const defaultRef = useRef(null);

  const draftKey = useMemo(() => `termsDraft:${norm(key) || DEFAULT_KEY}`, [key]);
  const lastDraftWriteRef = useRef(0);

  const persistLastKey = useCallback((k) => {
    try {
      localStorage.setItem(LAST_KEY_STORAGE, String(k || ""));
    } catch {}
  }, []);

  const persistPreset = useCallback((id) => {
    try {
      localStorage.setItem(LAST_PRESET_STORAGE, String(id || ""));
    } catch {}
  }, []);

  const writeDraft = useCallback(
    (nextForm) => {
      try {
        const now = Date.now();
        if (now - lastDraftWriteRef.current < 250) return;
        lastDraftWriteRef.current = now;
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            ts: now,
            key: norm(key) || DEFAULT_KEY,
            selectedId: selectedId ?? null,
            form: nextForm,
          })
        );
      } catch {}
    },
    [draftKey, key, selectedId]
  );

  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem(draftKey);
    } catch {}
  }, [draftKey]);

  const syncFormFromDoc = useCallback((doc) => {
    if (!doc) {
      setForm({ title: "", body_md: "", is_active: true, is_default: false });
      return;
    }

    const raw = String(doc.body_md || "");
    const html = isProbablyHtml(raw) ? raw : mdToHtmlBasic(raw);

    setForm({
      title: doc.title || "",
      body_md: html || "<p></p>",
      is_active: !!doc.is_active,
      is_default: !!doc.is_default,
    });
  }, []);

  const fetchList = useCallback(
    async (opts = {}) => {
      setLoading(true);
      setErr("");
      try {
        const nextKey = norm(opts.key ?? key) || DEFAULT_KEY;
        persistLastKey(nextKey);

        const paths = ["terms", "/api/terms"];
        const { data } = await tryPaths(
          (path) =>
            axiosInstance.get(path, {
              params: { key: nextKey, include_inactive: includeInactive },
            }),
          paths
        );

        const list = Array.isArray(data) ? data : [];
        setItems(list);

        const keepId = list.some((x) => x.id === selectedId) ? selectedId : list[0]?.id ?? null;
        setSelectedId(keepId);

        const doc = list.find((x) => x.id === keepId) || null;
        syncFormFromDoc(doc);
      } catch (e) {
        setErr(`Load failed: ${describeAxiosError(e)}`);
      } finally {
        setLoading(false);
      }
    },
    [includeInactive, key, persistLastKey, selectedId, syncFormFromDoc]
  );

  // Restore last key + preset on mount
  useEffect(() => {
    try {
      const savedPreset = norm(localStorage.getItem(LAST_PRESET_STORAGE));
      if (savedPreset && TERMS_KEY_PRESETS.some((p) => p.id === savedPreset)) {
        setPresetId(savedPreset);
      }
    } catch {}

    try {
      const savedKey = norm(localStorage.getItem(LAST_KEY_STORAGE));
      if (savedKey) setKey(savedKey);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial fetch (use last key if present)
  useEffect(() => {
    const initialKey = (() => {
      try {
        return norm(localStorage.getItem(LAST_KEY_STORAGE)) || "";
      } catch {
        return "";
      }
    })();
    fetchList({ key: initialKey || DEFAULT_KEY });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When preset changes, apply its key unless user already has a different key typed.
  useEffect(() => {
    const preset = TERMS_KEY_PRESETS.find((p) => p.id === presetId) || TERMS_KEY_PRESETS[0];
    persistPreset(preset.id);

    const current = norm(key);
    const knownKeys = new Set(TERMS_KEY_PRESETS.map((p) => p.key));
    if (!current || knownKeys.has(current)) {
      setKey(preset.key);
      persistLastKey(preset.key);
      fetchList({ key: preset.key });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetId]);

  // Draft restore for current key
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const ts = Number(parsed?.ts || 0);
      if (!ts || Date.now() - ts > 7 * 24 * 60 * 60 * 1000) return;

      const f = parsed?.form;
      if (f && typeof f === "object") {
        setForm({
          title: String(f.title ?? ""),
          body_md: String(f.body_md ?? "<p></p>"),
          is_active: !!f.is_active,
          is_default: !!f.is_default,
        });
      }
    } catch {}
  }, [draftKey]);

  useEffect(() => {
    writeDraft(form);
  }, [form, writeDraft]);

  const onSelect = useCallback(
    (id) => {
      setSelectedId(id);
      const doc = items.find((x) => x.id === id) || null;
      syncFormFromDoc(doc);
    },
    [items, syncFormFromDoc]
  );

  const getSubmitValues = useCallback(() => {
    const title = norm(titleRef.current?.value);
    const is_active = !!activeRef.current?.checked;
    const is_default = !!defaultRef.current?.checked;

    // Store HTML into body_md for WYSIWYG fidelity (schema-safe: still a string).
    const body_md = String(form.body_md ?? "").trim();

    return { title, body_md, is_active, is_default };
  }, [form.body_md]);

  const validateSubmit = useCallback(() => {
    const k = norm(key);
    if (!k) return "Key is required.";
    const { title, body_md } = getSubmitValues();
    if (!title) return "Title is required.";
    if (!String(body_md || "").trim()) return "Body is required.";
    return "";
  }, [getSubmitValues, key]);

  const patchTerm = useCallback(async (id, payload) => {
    const patchPaths = [`terms/${id}`, `/api/terms/${id}`];
    await tryPaths((path) => axiosInstance.patch(path, payload), patchPaths);
  }, []);

  const postTerm = useCallback(async (payload) => {
    const postPaths = ["terms", "/api/terms"];
    const { data } = await tryPaths((path) => axiosInstance.post(path, payload), postPaths);
    return data;
  }, []);

  const save = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const msg = validateSubmit();
      if (msg) throw new Error(msg);

      const v = getSubmitValues();
      const k = norm(key) || DEFAULT_KEY;

      if (selected?.id) {
        const payload = {
          title: v.title,
          body_md: v.body_md,
          is_active: !!v.is_active,
          is_default: !!v.is_default,
        };
        await patchTerm(selected.id, payload);
        await fetchList({ key: k });
        clearDraft();
        return;
      }

      // Create first version
      const payload = {
        key: k,
        version: maxVersion > 0 ? maxVersion + 1 : 1,
        title: v.title,
        body_md: v.body_md,
        is_active: !!v.is_active,
        is_default: !!v.is_default,
      };

      const wantsDefault = !!payload.is_default;
      if (wantsDefault && currentDefault?.id) payload.is_default = false;

      const created = await postTerm(payload);

      if (wantsDefault) {
        if (currentDefault?.id) {
          await patchTerm(currentDefault.id, { is_default: false });
        }
        if (created?.id) {
          await patchTerm(created.id, { is_default: true });
        }
      }

      await fetchList({ key: k });
      if (created?.id) setSelectedId(created.id);
      clearDraft();
    } catch (e) {
      setErr(`Save failed: ${describeAxiosError(e)}`);
    } finally {
      setLoading(false);
    }
  }, [
    clearDraft,
    currentDefault,
    fetchList,
    getSubmitValues,
    key,
    maxVersion,
    patchTerm,
    postTerm,
    selected,
    validateSubmit,
  ]);

  const newVersion = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const msg = validateSubmit();
      if (msg) throw new Error(msg);

      const v = getSubmitValues();
      const k = norm(key) || DEFAULT_KEY;

      const wantsDefault = !!v.is_default;

      const payload = {
        key: k,
        version: maxVersion + 1,
        title: v.title,
        body_md: v.body_md,
        is_active: true,
        is_default: false, // critical
      };

      const created = await postTerm(payload);

      if (wantsDefault) {
        if (currentDefault?.id) {
          await patchTerm(currentDefault.id, { is_default: false });
        }
        if (created?.id) {
          await patchTerm(created.id, { is_default: true });
        }
      }

      await fetchList({ key: k });
      if (created?.id) setSelectedId(created.id);
      clearDraft();
    } catch (e) {
      setErr(`New version failed: ${describeAxiosError(e)}`);
    } finally {
      setLoading(false);
    }
  }, [clearDraft, currentDefault, fetchList, getSubmitValues, key, maxVersion, patchTerm, postTerm, validateSubmit]);

  const onKeyInputKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        fetchList({ key: e.currentTarget.value });
      }
    },
    [fetchList]
  );

  const preset =
    TERMS_KEY_PRESETS.find((p) => p.id === presetId) ||
    TERMS_KEY_PRESETS[0];

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const content = contentRef.current;
    const viewport = versionsViewportRef.current;
    const firstVersionCard = firstVersionCardRef.current;

    if (!root || !content) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      const currentViewport = versionsViewportRef.current;

      if (currentViewport) {
        currentViewport.style.height = "";
        currentViewport.style.minHeight = "";
      }

      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const currentFirstVersionCard = firstVersionCardRef.current;
        const nextViewport = versionsViewportRef.current;

        if (currentFirstVersionCard && nextViewport) {
          const cardHeight = Math.ceil(
            Math.max(
              currentFirstVersionCard.getBoundingClientRect().height || 0,
              currentFirstVersionCard.scrollHeight || 0
            )
          );

          if (Number.isFinite(cardHeight) && cardHeight > 0) {
            nextViewport.style.height = `${cardHeight}px`;
            nextViewport.style.minHeight = `${cardHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";
        content.style.height = "auto";
        content.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    resizeObserver?.observe(content);

    if (resizeObserver && firstVersionCard) {
      resizeObserver.observe(firstVersionCard);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      if (viewport) {
        viewport.style.height = "";
        viewport.style.minHeight = "";
      }

      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";
    };
  }, [
    err,
    includeInactive,
    items.length,
    key,
    loading,
    presetId,
    selectedId,
  ]);

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-terms-versions-viewport {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .vx-terms-versions-viewport::-webkit-scrollbar {
            display: none;
          }

          .vx-terms-version-card {
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }

          .vx-terms-editor-body {
            min-height: 360px !important;
          }

          .vx-terms-touch-control {
            min-height: 44px;
          }
        }
      `}</style>
      {err ? (
        <div
          className="mb-3 break-words rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
          onMouseDown={stopGridDrag}
          onPointerDown={stopGridDrag}
          onTouchStart={stopGridDrag}
        >
          {String(err)}
        </div>
      ) : null}

      <div ref={contentRef} className="grid h-auto min-h-0 grid-cols-1 gap-3 md:h-full md:grid-cols-[320px_minmax(0,1fr)]">
        {/* LEFT */}
        <div className="h-auto min-h-0 rounded-xl border bg-white overflow-visible md:h-full md:overflow-hidden">
          <div className="flex flex-col gap-2 border-b px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm font-semibold">Versions</div>
            <button
              type="button"
              className="vx-terms-touch-control w-full rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-60 sm:w-auto"
              onMouseDown={stopGridDrag}
              onPointerDown={stopGridDrag}
              onTouchStart={stopGridDrag}
              onClick={() => fetchList()}
              disabled={loading}
            >
              Refresh
            </button>
          </div>

          <div className="h-auto min-h-0 overflow-visible p-3 md:h-full md:overflow-auto">
            <div className="grid gap-2 mb-3">
              <label className="text-xs font-medium text-gray-700">
                Type
                <select
                  value={presetId}
                  onChange={(e) => setPresetId(e.target.value)}
                  onMouseDown={stopGridDrag}
                  onPointerDown={stopGridDrag}
                  onTouchStart={stopGridDrag}
                  className="vx-terms-touch-control mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-2"
                  title="Switch between Quote Terms and Service Terms"
                >
                  {TERMS_KEY_PRESETS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-xs font-medium text-gray-700">
                Key (override)
                <input
                  value={key}
                  onChange={(e) => {
                    setKey(e.target.value);
                    persistLastKey(e.target.value);
                  }}
                  onBlur={() => fetchList({ key })}
                  onKeyDown={onKeyInputKeyDown}
                  onMouseDown={stopGridDrag}
                  onPointerDown={stopGridDrag}
                  onTouchStart={stopGridDrag}
                  className="vx-terms-touch-control mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-2"
                  type="text"
                  placeholder={preset?.key || "quotes_terms"}
                />
              </label>

              <div className="text-[11px] text-gray-500">
                Preset key: <span className="font-mono">{preset?.key}</span>
              </div>

              <label className="flex min-h-[44px] items-center gap-2 text-xs text-gray-700 select-none md:min-h-0">
                <input
                  className="h-5 w-5 md:h-auto md:w-auto"
                  type="checkbox"
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  onMouseDown={stopGridDrag}
                  onPointerDown={stopGridDrag}
                  onTouchStart={stopGridDrag}
                />
                Include inactive
              </label>
            </div>

            <div ref={versionsViewportRef} className="vx-terms-versions-viewport grid h-auto gap-2 overflow-visible md:h-auto md:overflow-visible">
              {items.map((x, index) => {
                const active = x.id === selectedId;
                return (
                  <button
                    key={x.id}
                    ref={index === 0 ? firstVersionCardRef : null}
                    type="button"
                    onMouseDown={stopGridDrag}
                    onPointerDown={stopGridDrag}
                    onTouchStart={stopGridDrag}
                    onClick={() => onSelect(x.id)}
                    className={[
                      "vx-terms-version-card min-w-0 w-full rounded-xl border p-3 text-left",
                      active ? "border-[var(--accent-color)]" : "hover:bg-gray-50",
                    ].join(" ")}
                  >
                    <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-xs font-semibold">
                        v{x.version} {x.is_default ? "• Default" : ""}
                      </div>
                      <div className="break-words text-xs text-gray-600">{x.is_active ? "Active" : "Inactive"}</div>
                    </div>
                    <div className="mt-1 break-words text-sm font-medium">{x.title}</div>
                    <div className="mt-1 break-words text-xs text-gray-500">Updated {gbDateTime(x.updated_at)}</div>
                  </button>
                );
              })}

              {!items.length ? (
                <div className="rounded-lg border border-dashed p-3 text-sm text-gray-600">No terms found for this key.</div>
              ) : null}
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="h-auto min-h-0 rounded-xl border bg-white overflow-visible md:h-full md:overflow-hidden">
          <div className="flex flex-col gap-2 border-b px-3 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="text-sm font-semibold">Editor</div>
            <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:flex-wrap">
              <button
                type="button"
                className="vx-terms-touch-control w-full rounded-lg bg-btnActionNormal px-3 py-2 text-sm text-white hover:opacity-90 disabled:opacity-60 sm:w-auto"
                onMouseDown={stopGridDrag}
                onPointerDown={stopGridDrag}
                onTouchStart={stopGridDrag}
                onClick={save}
                disabled={loading}
                title={selected ? "Updates selected version." : "Creates a new version if none selected."}
              >
                Save
              </button>

              <button
                type="button"
                className="vx-terms-touch-control w-full rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-60 sm:w-auto"
                onMouseDown={stopGridDrag}
                onPointerDown={stopGridDrag}
                onTouchStart={stopGridDrag}
                onClick={newVersion}
                disabled={loading}
                title="Creates a new version row (keeps history). If 'Default' is ticked, it will swap defaults safely."
              >
                New Version
              </button>
            </div>
          </div>

          <div className="h-auto min-h-0 overflow-visible p-3 md:h-full md:overflow-auto">
            <div className="grid gap-3">
              <label className="text-xs font-medium text-gray-700">
                Title
                <input
                  ref={titleRef}
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  onMouseDown={stopGridDrag}
                  onPointerDown={stopGridDrag}
                  onTouchStart={stopGridDrag}
                  className="vx-terms-touch-control mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-2"
                  type="text"
                  placeholder="Terms & Conditions"
                />
              </label>

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                <label className="flex min-h-[44px] items-center gap-2 text-xs text-gray-700 select-none md:min-h-0">
                  <input
                    ref={activeRef}
                    className="h-5 w-5 md:h-auto md:w-auto"
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                    onMouseDown={stopGridDrag}
                    onPointerDown={stopGridDrag}
                    onTouchStart={stopGridDrag}
                  />
                  Active
                </label>

                <label className="flex min-h-[44px] items-center gap-2 text-xs text-gray-700 select-none md:min-h-0">
                  <input
                    ref={defaultRef}
                    className="h-5 w-5 md:h-auto md:w-auto"
                    type="checkbox"
                    checked={form.is_default}
                    onChange={(e) => setForm((p) => ({ ...p, is_default: e.target.checked }))}
                    onMouseDown={stopGridDrag}
                    onPointerDown={stopGridDrag}
                    onTouchStart={stopGridDrag}
                  />
                  Default for this key
                </label>
              </div>

              <div className="text-xs font-medium text-gray-700">Body (WYSIWYG)</div>
              <RichEditor
                value={form.body_md}
                onChange={(html) => setForm((p) => ({ ...p, body_md: html }))}
                disabled={loading}
              />

              <div className="text-xs text-gray-600">
                {selected ? (
                  <>
                    Selected: id={selected.id}, v{selected.version} • Updated {gbDateTime(selected.updated_at)}
                  </>
                ) : (
                  <>No selected version.</>
                )}{" "}
                • Current default: {currentDefault ? `v${currentDefault.version}` : "none"} • Next version: v{maxVersion + 1}
              </div>

              <div className="break-words rounded-lg border bg-gray-50 px-3 py-2 text-[11px] text-gray-600">
                Stored field: <span className="font-mono">terms_documents.body_md</span> now contains HTML for WYSIWYG fidelity.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
